'use client';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ChevronDown, Copy, ChevronRight, QrCode } from 'lucide-react';
import { useLanguage } from '@/components/LanguageProvider';
import { getSocials } from '@/components/config/socials';
import { useServerIp } from '@/hooks/useServerIp'; 


export default function Hero({ onOpenTrailer }: { onOpenTrailer: () => void }) {
  const { t, locale } = useLanguage();
  const tl = t.landing.hero;
  
  // Используем единый менеджер IP
  const { javaIp } = useServerIp();

  const [copied, setCopied] = useState(false);
  const activeSocials = getSocials(locale).filter(s => s.showInHero);

  const [wordIndex, setWordIndex] = useState(0);

  const heroImg = locale === 'en' ? "/landing2.0/kizrum_11zon.webp" : "/landing2.0/kizrum_11zon.webp";

  const copyIp = () => {
    navigator.clipboard.writeText(javaIp);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    const interval = setInterval(() => setWordIndex((prev) => (prev + 1) % tl.words.length), 3000);
    return () => clearInterval(interval);
  }, [tl.words.length]);

  return (
    <section className="relative w-full h-[35rem] md:h-[40rem] xl:h-[48rem] border border-white/10 rounded-[1.25rem] md:rounded-[1.875rem] overflow-hidden isolate bg-[#111111]">
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/60 via-transparent to-black/60 z-[5]" />

      <motion.div className="absolute inset-0 z-0">
        <img src={heroImg} alt="banner" className="w-full h-full object-cover object-center" />
      </motion.div>

      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[90%] md:max-w-[700px] z-20 pointer-events-none pt-4 md:pt-8 px-4 flex flex-col items-center">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-black leading-[1] tracking-tighter text-white uppercase text-center drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)]">
             {tl.title1} <br className="hidden sm:block" /> {tl.title2}
          </h1>
          <div className="mt-4 md:mt-5 flex justify-center w-full min-h-[60px] md:min-h-[80px] relative">
             <motion.div layout transition={{ type: "spring", stiffness: 400, damping: 30 }} className="bg-black/40 backdrop-blur-md border border-white/10 rounded-[12px] md:rounded-[16px] shadow-xl overflow-hidden flex items-center justify-center">
               <AnimatePresence mode="popLayout">
                  <motion.div key={wordIndex} initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -30, opacity: 0 }} transition={{ duration: 0.3 }} className="px-5 pt-3 pb-1 md:px-6 md:pt-4 md:pb-1.5 flex items-center justify-center will-change-transform transform-gpu">
                     <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#667fff] to-[#a855f7] font-mc text-4xl sm:text-5xl md:text-[4.5rem] leading-none whitespace-nowrap">
                        {tl.words[wordIndex]}
                     </span>
                  </motion.div>
               </AnimatePresence>
             </motion.div>
          </div>
      </div>

      <div className="absolute inset-0 m-auto w-20 h-20 md:w-24 md:h-24 z-30">
         <button onClick={onOpenTrailer} className="group w-full h-full rounded-full bg-black/40 border border-white/20 hover:bg-[#667fff] hover:border-transparent transition-all duration-300 flex items-center justify-center outline-none focus:outline-none shadow-2xl cursor-pointer">
            <Play size={32} className="text-white translate-x-[2px] drop-shadow-xl" fill="currentColor" />
         </button>
      </div>

      <div className="absolute right-8 top-1/2 -translate-y-1/2 z-[30] hidden lg:flex flex-col items-center gap-4">
         <div className="w-[1px] h-16 bg-gradient-to-b from-transparent to-white/20" />
         <div className="flex flex-col items-center gap-3 mt-2 mb-2 pointer-events-none">
            <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-white/50 [writing-mode:vertical-rl] rotate-180">{tl.subscribe}</span>
            <motion.div animate={{ y: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }} className="will-change-transform transform-gpu">
              <ChevronDown size={14} className="text-white/50" />
            </motion.div>
         </div>
        <div className="flex flex-col gap-3">
          {activeSocials.map((social) => {
            const Icon = social.icon;
            return (
              <a 
                key={social.id} 
                href={social.href} 
                target="_blank" 
                className="group relative w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/50 hover:bg-white/10 hover:text-white hover:scale-110 transition-all duration-300 cursor-pointer"
              >
                <Icon className={`shrink-0 ${social.heroIconClass}`} />
                <span className="absolute right-12 top-1/2 -translate-y-1/2 opacity-0 translate-x-2 group-hover:translate-x-0 group-hover:opacity-100 transition-all text-white text-[11px] font-bold uppercase tracking-wider bg-[#111111] px-3 py-1.5 rounded-lg border border-white/10 origin-right whitespace-nowrap">
                  {social.label}
                </span>
              </a>
            );
          })}
        </div>

        <div className="w-[1px] h-16 bg-gradient-to-t from-transparent to-white/20 mt-2" />
      </div> 

      <div className="absolute bottom-6 md:bottom-10 left-0 w-full flex flex-col items-center justify-center gap-3 px-4 z-30 pointer-events-none">
        <p className="text-sm md:text-base text-[#aaaaaa] font-medium text-center drop-shadow-md bg-black/40 px-5 py-2 rounded-xl border border-white/10 mb-2 pointer-events-auto">
          {tl.subtitle} <span className="text-[#667fff] font-semibold text-white">{tl.subtitleHighlight}</span>
        </p>
        <div className="flex items-center gap-3 w-full justify-center pointer-events-auto">
            <div className="w-full max-w-[400px] bg-[#111111]/95 border border-white/10 rounded-2xl p-2 pl-4 flex items-center justify-between shadow-lg">
              
              <div 
                onClick={copyIp} 
                data-track="ip_button_clicked" 
                data-track-label="hero_ip"
                className="flex items-center gap-2.5 cursor-pointer group truncate"
              >
                <div className="w-2 h-2 rounded-full bg-[#4cc91f] shadow-[0_0_10px_#4cc91f] animate-pulse shrink-0"></div>
                <span className="text-white/70 font-mc text-sm md:text-base uppercase group-hover:text-white transition-colors truncate mt-0.5 cursor-pointer">
                  {copied ? tl.copied : javaIp}
                </span>
                {!copied && <Copy size={16} className="text-white/30 group-hover:text-white transition-colors hidden sm:block" />}
              </div>
              
              <a 
                href="#access" 
                data-track="play_button_clicked" 
                data-track-label="hero_play"
                className="h-10 px-5 rounded-xl bg-[#667fff] hover:bg-[#7a93ff] text-white font-medium transition-all flex items-center gap-1.5 outline-none text-sm shrink-0 cursor-pointer"
              >
                {tl.playBtn}<ChevronRight size={16} />
              </a>
            </div>
            <div className="relative w-14 h-14 shrink-0 hidden sm:block">
               <div className="absolute bottom-0 left-0 w-14 h-14 hover:w-[200px] hover:h-[200px] bg-[#111111]/95 border border-white/10 rounded-2xl transition-all duration-300 flex items-center justify-center group overflow-hidden cursor-crosshair origin-bottom-left shadow-lg z-50">
                  <QrCode size={20} className="absolute text-white/70 group-hover:opacity-0 transition-opacity duration-200" />
                  <div className="absolute bottom-0 left-0 w-[200px] h-[200px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100 pointer-events-none">
                      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=https://${javaIp}&bgcolor=111111&color=ffffff&margin=0`} alt="QR" className="w-full h-full object-cover rounded-2xl" />
                  </div>
               </div>
            </div>
        </div>
      </div>
    </section>
  );
}